import moment from 'moment';
import { convertDate, convertPercentage, convertNumberBR } from 'xpi-black-ui/Utils/helpers';

import { convertNumberBR as convertNumberBRFraction, convertToCurrency } from 'utils/helpers';

import { getAssetImage, requestAssetImage } from 'utils/assetImage';

const getIsDol = asset => asset === 'DOL';

export const showAssetDescription = (assetType) => {
  const blackList = [1, 5, 6];
  return !blackList.includes(assetType);
};

export const valueInPercentage = (props) => {
  const { value, multiplier, reference } = props;

  let newValue = value;

  newValue = ((newValue / (reference * multiplier)) * 100);

  return `${convertNumberBR(newValue, 2)}%`;
};

const getRebate = (legs) => {
  const leg = legs?.filter(perna => perna.rebate > 0)[0];

  if (leg) {
    return leg.rebate;
  }

  return 0;
};

const getRubiYieldRebate = (legs) => {
  const leg = legs?.filter(perna => perna.percentualRebate > 0)[0];

  if (leg) {
    return leg.percentualRebate;
  }

  return 0;
};

export const getRebatePercentage = (legs, reference) => {
  const rebate = getRebate(legs);

  if (rebate) {
    return valueInPercentage({ value: rebate, multiplier: 1, reference });
  }
  return rebate;
};

export const getRubiYieldRebatePercentage = (legs, reference) => {
  const rebate = getRubiYieldRebate(legs);

  if (rebate) {
    return valueInPercentage({ value: rebate, multiplier: 1, reference });
  }
  return rebate;
};

export const getRebateCurrency = (legs, multiplicador) => {
  const rebate = getRebate(legs);

  if (rebate) {
    return convertToCurrency(rebate * multiplicador);
  }
  return rebate;
};

export const getRubiYieldRebateCurrency = (legs, multiplicador) => {
  const rebate = getRubiYieldRebate(legs);

  if (rebate) {
    return convertToCurrency(rebate * multiplicador);
  }
  return rebate;
};

const getImage = async (product) => {
  let image;

  image = await getAssetImage({ name: product.ativo });

  if (!image) {
    image = await requestAssetImage({ urlImagemAtivo: product.urlImagemAtivo });
  }

  return image;
};

const precoUnidadeMedidaFormatado = (unidadeMedida) => {
  if (unidadeMedida && (unidadeMedida.lastIndexOf('s') + 1) === unidadeMedida.length) {
    return unidadeMedida.substr(0, unidadeMedida.lastIndexOf('s'));
  }
  return unidadeMedida;
};

const getCustomTooltip = (tooltipMsg, multiplicador, isDol) => {
  let tooltip = tooltipMsg;

  if (multiplicador && multiplicador > 1) {
    tooltip = `${tooltip} * Unidade de negociação`;
  }

  if (isDol) {
    tooltip = `${tooltip} * Cotação dólar`;
  }

  return tooltip;
};

const getTooltipPrecoOperacao = (multiplicador, isDol) => {
  const tooltipMsg = 'Como calcular em R$: Preço referência * Preço da operação %';

  return getCustomTooltip(tooltipMsg, multiplicador, isDol);
};

const getTooltipComissaoDividir = (multiplicador, isDol) => {
  const tooltipMsg = 'Como calcular em R$: Preço referência * Comissão da operação %';

  return getCustomTooltip(tooltipMsg, multiplicador, isDol);
};

export const rubiYieldFormatter = async (product) => {
  const isDol = false;

  const { precoReferencia } = product;

  const rebatePercentual = getRubiYieldRebatePercentage(product.pernas, precoReferencia.valor);

  return {
    ...product,
    isDol,
    imagem: product.ativo.imagemBase64,
    isBDR: false,
    disabledBasketClients: false,
    codigoOperacao: product.id,
    tooltipPrecoOperecao: getTooltipPrecoOperacao(1, isDol),
    tooltipComissaDividir: getTooltipComissaoDividir(1, isDol),
    descricaoAtivo: 'B3',
    unidadeMedida: null,
    multiplicadorFormatado: convertNumberBR(1),
    precoUnidadeMedida: null,
    lucroMaximoFormatado: product.lucroMaximo.valor === -1 ? 'Ilimitado' : convertToCurrency(product.lucroMaximo.valor),
    lucroMaximoPercentual: valueInPercentage({ value: product.lucroMaximo.valor, multiplier: 1, reference: precoReferencia.valor }),
    prejuizoMaximoFormatado: product.prejuizoMaximo.valor === -1 ? 'Ilimitado' : convertToCurrency(product.prejuizoMaximo.valor),
    prejuizoMaximoPercentual: valueInPercentage({ value: product.prejuizoMaximo.valor, multiplier: 1, reference: precoReferencia.valor }),
    vencimentoFormatado: convertDate(product.dataFixing),
    precoOperacaoPercentual: product.percentualPrecoUnitarioCliente,
    precoOperacaoFormatado: convertToCurrency(product.precoUnitarioCliente.valor * 1),
    comissaoPercentual: `${convertNumberBRFraction(product.percentualComissaoTotal.toFixed(2), 2)} %`,
    comissaoFormatado: convertToCurrency(product.comissaoTotal.valor),
    precoReferenciaFormatado: convertNumberBRFraction(precoReferencia.valor, 2),
    termoFormatado: null,
    cotacaoFormatado: null,
    limiteFormatado: convertNumberBR(product.limiteDisponivel.valor, 2),
    tamanhoLote: 100,
    loteMinimoFormatado: convertNumberBR(product.loteMinimo.valor, 2),
    dataLimitReserva: convertDate(moment()),
    cenariosVencimento: product.estrutura.cenariosVencimentos.map(scenario => ({
      ...scenario,
      texto: scenario.descricao.replace(/\\n/g, ' ').replace(/\/n/g, ' '),
    })),
    temRebate: Boolean(getRubiYieldRebate(product.pernas)),
    rebateFormatado: getRubiYieldRebateCurrency(product.pernas, 1),
    rebatePercentual,
    pernas: product.pernas.map(leg => ({
      ...leg,
      natureza: leg.lado,
      tipoEstrutura: leg.tipo.name === 'Stock' ? 'da ação' : leg.tipo.name,
      preEquivalentRateFormattedIDI: null,
      strikeFormatado: convertPercentage(leg.percentualStrike, 2),
      strikeFormattedIDI: convertToCurrency(leg.strike, 'BRL', 2),
      barrierFormattedIDI: convertToCurrency(leg.barreira, 'BRL', 2),
      barreiraFormatado: convertPercentage(leg.percentualBarreira, 2),
      formattedRebate: rebatePercentual,
      percentualLimitador: leg.percentualLimitador,
      percentualLimitadorFormatado: convertPercentage(leg.percentualLimitador),
    })),
    cenarios: product.performances.map(scenario => ({
      ...scenario,
      variacaoFormatado: convertPercentage(scenario.variacaoPapel, 2),
      payoffFormatado: convertPercentage(scenario.variacaoEstrategia, 2),
    })),
  };
};

export default async (product) => {
  const isDol = getIsDol(product.ativo);

  const imagem = await getImage(product);
  const isAssetIDI = product.ativo === 'IDI';

  const { precoReferencia, multiplicador, precoOperacao } = product;
  const precoOperacaoPercentual = valueInPercentage({ value: precoOperacao * multiplicador, multiplier: multiplicador, reference: precoReferencia });

  const rebatePercentual = getRebatePercentage(product.pernas, precoReferencia);

  return {
    ...product,
    isDol,
    imagem,
    isBDR: product.tipoAtivo === 6,
    disabledBasketClients: product.tipoAtivo === 3 || product.tipoAtivo === 7,
    tooltipPrecoOperecao: getTooltipPrecoOperacao(product?.multiplicador, isDol),
    tooltipComissaDividir: getTooltipComissaoDividir(product?.multiplicador, isDol),
    descricaoAtivo: showAssetDescription(product.tipoAtivo) ? product.descricaoAtivo : null,
    unidadeMedida: product?.unidadeMedida?.toLowerCase(),
    multiplicadorFormatado: convertNumberBR(product.multiplicador),
    precoUnidadeMedida: precoUnidadeMedidaFormatado(product?.unidadeMedida)?.toLowerCase(),
    lucroMaximoFormatado: product.lucroMaximo === -1 ? 'Ilimitado' : convertToCurrency(product.lucroMaximo),
    lucroMaximoPercentual: valueInPercentage({ value: product.lucroMaximo, multiplier: multiplicador, reference: precoReferencia }),
    prejuizoMaximoFormatado: product.prejuizoMaximo === -1 ? 'Ilimitado' : convertToCurrency(product.prejuizoMaximo),
    prejuizoMaximoPercentual: valueInPercentage({ value: product.prejuizoMaximo, multiplier: multiplicador, reference: precoReferencia }),
    vencimentoFormatado: convertDate(product.vencimento),
    precoOperacaoPercentual,
    precoOperacaoFormatado: convertToCurrency(precoOperacao * multiplicador * (product.cotacao || 1)),
    comissaoPercentual: `${convertNumberBRFraction(product.pctComissao.toFixed(2), 2)} %`,
    comissaoFormatado: convertToCurrency(product.comissao),
    precoReferenciaFormatado: convertNumberBRFraction(precoReferencia, 2),
    termoFormatado: convertNumberBRFraction(product.termo, 2),
    cotacaoFormatado: convertToCurrency(product.cotacao, 'BRL', 5),
    limiteFormatado: convertNumberBR(product.limite, 2),
    loteMinimoFormatado: convertNumberBR(product.loteMinimo, 2),
    dataLimitReserva: convertDate(moment()),
    cenariosVencimento: product.cenariosVencimento.map(scenario => ({
      ...scenario,
      texto: scenario.texto.replace(/\\n/g, ' ').replace(/\/n/g, ' '),
    })),
    temRebate: Boolean(getRebate(product.pernas)),
    rebateFormatado: getRebateCurrency(product.pernas, multiplicador),
    rebatePercentual,
    pernas: product.pernas.map(leg => ({
      ...leg,
      natureza: leg.natureza === 'C' ? 'Compra' : 'Venda',
      tipoEstrutura: leg.tipoEstrutura === 'SPOT' ? 'da ação' : leg.tipoEstrutura,
      preEquivalentRateFormattedIDI: convertPercentage(leg.preEquivalentRate, 2),
      strikeFormatado: convertPercentage(leg.strikePercentual, 2),
      strikeFormattedIDI: convertToCurrency(leg.strike, 'BRL', 2),
      barrierFormattedIDI: convertToCurrency(leg.valorBarreira, 'BRL', 2),
      barreiraFormatado: convertPercentage(leg.strikeBarreira, 2),
      formattedRebate: rebatePercentual,
    })),
    cenarios: product.cenarios.map(scenario => ({
      ...scenario,
      variacaoFormatado: convertPercentage(scenario.variacao, 2),
      payoffFormatado: isAssetIDI ? convertToCurrency(scenario.payoff, 'BRL', 2)?.replace('R$', '') : convertPercentage(scenario.payoff, 2),
    })),
  };
};
